import { Agent } from '@kanbu/schema/enums';
import { Trans } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShoppingCartIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { aiCoreClient } from '@/services/ai-core-client';
import { Label } from '@/ui/label';
import { Switch } from '@/ui/switch';

interface AgentTypeToggleProps {
  chatId: string;
  onECommerceToggle?: (enabled: boolean) => void;
  className?: string;
}

export function AgentTypeToggle({
  chatId,
  onECommerceToggle,
  className,
}: AgentTypeToggleProps) {
  const [eComEnabled, setEComEnabled] = useState(false);
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['agentSummary', chatId],
    queryFn: () => aiCoreClient.agents.summary({ agentId: chatId }),
  });

  useEffect(() => {
    setEComEnabled(data?.agent.agent === Agent.ShopAssistant);
  }, [data]);

  const { mutateAsync: updateAgent, isPending: isUpdatingAgent } = useMutation({
    mutationFn: (agent: Agent) => aiCoreClient.agents.update({ chatId, agent }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agentSummary', chatId] });
    },
  });

  return (
    <div
      className={`flex items-center shrink-0 border-input has-data-[state=checked]:border-primary/50 relative gap-6 rounded-md p-[3px] outline-none border bg-background/50 ${className ?? ''}`}
    >
      <Switch
        id='ecom-switch'
        className='order-1 h-4 w-6 after:absolute after:inset-0 [&_span]:size-3 data-[state=checked]:[&_span]:translate-x-2 data-[state=checked]:[&_span]:rtl:-translate-x-2 mr-1'
        aria-describedby='ecom-switch-description'
        disabled={isUpdatingAgent}
        onCheckedChange={async value => {
          try {
            await updateAgent(
              value ? Agent.ShopAssistant : Agent.Conversational,
            );

            setEComEnabled(value);

            if (onECommerceToggle) {
              onECommerceToggle(value);
            }
          } catch (error) {
            console.error(error);
          }
        }}
        checked={eComEnabled}
      />
      <div className='flex grow items-center gap-2'>
        <span className='size-6 bg-primary/25 rounded-[10px] flex items-center justify-center  border-primary/30 border'>
          <ShoppingCartIcon className='size-3.5 text-primary' />
        </span>
        <div className='grid grow gap-2'>
          <Label htmlFor='ecom-switch'>
            <Trans>E-commerce</Trans>
          </Label>
        </div>
      </div>
    </div>
  );
}
